// src/services/insurancePolicyService.js
import { useAuth } from "../hooks/useAuth";
import API_CONFIG from "./ApiConfig";
import { DEFAULT_URL } from "./ApiConfig";
/**
 * Service for handling insurance policy uploads and check-ins
 */
const insurancePolicyService = {
  /**
   * Upload a file to the server and get back the URL
   *
   * @param {File} file - The file to upload
   * @param {Object} userData - User data including userId
   * @param {Object} deviceInfo - Device information
   * @returns {Promise<string>} - URL of the uploaded file
   */
  uploadFile: async (file, firebaseToken) => {
    try {
      console.error(file);
      let deviceInfo = {};
      // Convert file to base64
      const base64File = await fileToBase64(file);
      console.error(base64File);
      // Prepare payload for file upload
      const payload = {
        name: file.name,
        deviceManufacturer: deviceInfo.manufacturer || "Web",
        deviceModel: deviceInfo.model || "Browser",
        deviceOS: deviceInfo.os || navigator.userAgent,
        deviceIdentifier:
          deviceInfo.identifier ||
          (crypto.randomUUID
            ? crypto.randomUUID()
            : "web_" +
              Date.now() +
              "_" +
              Math.random().toString(36).substring(2, 15)),
        deviceType: deviceInfo.type || "Web",
        latitude: deviceInfo.latitude || 0,
        longitude: deviceInfo.longitude || 0,
        file: base64File,
      };

      // Make API call to upload endpoint
      let uri = process.env.REACT_APP_API_BASE_URL || DEFAULT_URL;
      const API_URL = uri || "https://api.ownli.co";

      const response = await fetch(`${API_URL}/api/file/upload`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-user-token": firebaseToken,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "File upload failed");
      }

      const data = await response.json();
      return data.fileUrl; // Return the URL from the response
    } catch (error) {
      console.error("Error uploading file:", error);
      throw error;
    }
  },

  /**
   * Submit policy check-in data with the uploaded file URL
   *
   * @param {string} fileUrl - URL of the uploaded file
   * @param {Object} policyData - Policy data
   * @param {string} documentType - Type of document (e.g., "DEC_AUTO", "INSURANCE_CARD")
   * @returns {Promise<Object>} - API response
   */
  submitPolicyCheckin: async (fileUrl, firebaseToken) => {
    try {
      // Prepare payload for policy check-in
      const payload = {
        userId: "noop",
        policyType: "AUTO",
        files: [
          {
            url: fileUrl,
            documentType: "DEC_AUTO",
            type: "DOCUMENT",
          },
        ],
        latitude: 0.0,
        longitude: 0.0,
      };

      let uri = process.env.REACT_APP_API_BASE_URL || DEFAULT_URL;
      const API_URL = uri || "https://api.ownli.co";

      const response = await fetch(
        `${API_URL}/api/users/checkin/insurance-policy`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-user-token": firebaseToken,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Policy check-in failed");
      }

      return await response.json();
    } catch (error) {
      console.error("Error submitting policy check-in:", error);
      throw error;
    }
  },

  /**
   * Submit policy check-in data with the uploaded file URL
   *
   * @param {string} fileUrl - URL of the uploaded file
   * @param {Object} policyData - Policy data
   * @param {string} documentType - Type of document (e.g., "DEC_AUTO", "INSURANCE_CARD")
   * @returns {Promise<Object>} - API response
   */
  submitPolicyCheckinForExtendedWarranty: async (fileUrl, firebaseToken) => {
    try {
      // Prepare payload for policy check-in
      const payload = {
        userId: "noop",
        policyType: "AUTO",
        files: [
          {
            url: fileUrl,
            documentType: "DEC_AUTO",
            type: "DOCUMENT",
          },
        ],
        latitude: 0.0,
        longitude: 0.0,
      };

      let uri = process.env.REACT_APP_API_BASE_URL || DEFAULT_URL;
      const API_URL = uri || "https://api.ownli.co";

      const response = await fetch(
        `${API_URL}/api/extended-warranty/checkin/insurance-policy`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-user-token": firebaseToken,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error_message || "Policy check-in failed");
      }

      return await response.json();
    } catch (error) {
      console.error("Error submitting policy check-in:", error);
      throw error;
    }
  },

  /**
   * Submit policy check-in data with the uploaded file URL
   *
   * @param {string} fileUrl - URL of the uploaded file
   * @param {Object} policyData - Policy data
   * @param {string} documentType - Type of document (e.g., "DEC_AUTO", "INSURANCE_CARD")
   * @returns {Promise<Object>} - API response
   */
  submitPolicyCheckinForRefinancing: async (fileUrl, firebaseToken) => {
    try {
      // Prepare payload for policy check-in
      const payload = {
        userId: "noop",
        policyType: "AUTO",
        files: [
          {
            url: fileUrl,
            documentType: "DEC_AUTO",
            type: "DOCUMENT",
          },
        ],
        latitude: 0.0,
        longitude: 0.0,
      };

      let uri = process.env.REACT_APP_API_BASE_URL || DEFAULT_URL;
      const API_URL = uri || "https://api.ownli.co";

      const response = await fetch(
        `${API_URL}/api/refinancing/checkin/insurance-policy`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-user-token": firebaseToken,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error_message || "Policy check-in failed");
      }

      return await response.json();
    } catch (error) {
      console.error("Error submitting policy check-in:", error);
      throw error;
    }
  },

  /**
   * Submit policy check-in data with the uploaded file URL
   *
   * @param {string} fileUrl - URL of the uploaded file
   * @param {Object} policyData - Policy data
   * @param {string} documentType - Type of document (e.g., "DEC_AUTO", "INSURANCE_CARD")
   * @returns {Promise<Object>} - API response
   */
  submitPolicyCheckinForOCR: async (policyId, firebaseToken) => {
    try {
      // Prepare payload for policy check-in
      const payload = {
        policyId: policyId,
      };

      let uri = DEFAULT_URL;
      const API_URL = DEFAULT_URL;

      const response = await fetch(`${API_URL}/api/insurance/ocr/extract`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-user-token": firebaseToken,
        },
        body: policyId,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error_message || "Policy OCR failed");
      }

      return await response.json();
    } catch (error) {
      console.error("Error submitting policy check-in:", error);
      throw error;
    }
  },

  /**
   * Process complete policy upload workflow: upload file and submit check-in
   *
   * @param {File} file - The file to upload
   * @param {Object} userData - User data including userId
   * @param {Object} policyData - Additional policy data
   * @param {string} documentType - Type of document (e.g., "DEC_AUTO", "INSURANCE_CARD")
   * @returns {Promise<Object>} - Result with both upload and check-in information
   */
  processPolicyDocumentForExtendedWarranty: async (file, firebaseToken) => {
    let fileUrl = null;

    try {
      console.error("processPolicyDocument", file);
      // Step 1: Upload the file and get the URL
      fileUrl = await insurancePolicyService.uploadFile(file, firebaseToken);

      // Step 2: Submit the policy check-in with the file URL
      const result = await insurancePolicyService.submitPolicyCheckinForExtendedWarranty(
        fileUrl,
        firebaseToken
      );

      return {
        success: true,
        result,
        fileUrl,
        message: "Document uploaded and policy check-in submitted successfully",
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to process policy document",
        fileUrl,
      };
    }
  },

  /**
   * Process complete policy upload workflow: upload file and submit check-in
   *
   * @param {File} file - The file to upload
   * @param {Object} userData - User data including userId
   * @param {Object} policyData - Additional policy data
   * @param {string} documentType - Type of document (e.g., "DEC_AUTO", "INSURANCE_CARD")
   * @returns {Promise<Object>} - Result with both upload and check-in information
   */
  processPolicyDocumentForRefinancing: async (file, firebaseToken) => {
    let fileUrl = null;

    try {
      console.error("processPolicyDocument", file);
      // Step 1: Upload the file and get the URL
      fileUrl = await insurancePolicyService.uploadFile(file, firebaseToken);

      // Step 2: Submit the policy check-in with the file URL
      const result = await insurancePolicyService.submitPolicyCheckinForRefinancing(
        fileUrl,
        firebaseToken
      );

      return {
        success: true,
        result,
        fileUrl,
        message: "Document uploaded and policy check-in submitted successfully",
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to process policy document",
        fileUrl,
      };
    }
  },

  /**
   * Process complete policy upload workflow: upload file and submit check-in
   *
   * @param {File} file - The file to upload
   * @param {Object} userData - User data including userId
   * @param {Object} policyData - Additional policy data
   * @param {string} documentType - Type of document (e.g., "DEC_AUTO", "INSURANCE_CARD")
   * @returns {Promise<Object>} - Result with both upload and check-in information
   */
  processPolicyDocument: async (file, firebaseToken) => {
    try {
      console.error("processPolicyDocument", file);
      // Step 1: Upload the file and get the URL
      const fileUrl = await insurancePolicyService.uploadFile(
        file,
        firebaseToken
      );

      // Step 2: Submit the policy check-in with the file URL
      const result = await insurancePolicyService.submitPolicyCheckin(
        fileUrl,
        firebaseToken
      );
      try {
        await insurancePolicyService.submitPolicyCheckinForOCR(
          result.id,
          firebaseToken
        );
      } catch (e) {
        //ignore the error - we need to downstream figure out what
        //needs to be done if OCR fails
      }

      return {
        success: true,
        result,
        fileUrl,
        message: "Document uploaded and policy check-in submitted successfully",
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to process policy document",
      };
    }
  },

  getUserPolicies: async (firebaseToken) => {
    try {
      const url = API_CONFIG.getUrl(
        API_CONFIG.ENDPOINTS.USER.CHECKINS.INSURANCE_POLICY
      );
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-user-token": firebaseToken,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch policies: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error fetching user policies:", error);
      throw error;
    }
  },

  getExpiringUserPolicies: async (firebaseToken) => {
    try {
      const url = API_CONFIG.getUrl(
        API_CONFIG.ENDPOINTS.USER.CHECKINS.EXPIRING_INSURANCE_POLICY
      );
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-user-token": firebaseToken,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch policies: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error fetching user policies:", error);
      throw error;
    }
  },

  /**
   * Get all connected insurance policies for a user
   *
   * @param {string} userId - Firebase user ID
   * @returns {Promise<Array>} - Array of connected policy objects
   */
  getConnectedPolicies: async (firebaseToken) => {
    try {
      const url = API_CONFIG.getUrl(
        API_CONFIG.ENDPOINTS.CANOPY.CONNECTED_POLICY
      );
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-user-token": firebaseToken,
        },
      });

      if (!response.ok) {
        throw new Error(
          `Failed to fetch connected policies: ${response.status}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error("Error fetching connected policies:", error);
      throw error;
    }
  },

  /**
   * Check if a user has any existing policies (manual or connected)
   *
   * @param {string} userId - Firebase user ID
   * @returns {Promise<Object>} - Policy information or null if none found
   */
  checkExistingPolicies: async (firebaseToken) => {
    try {
      // First check for manual policies
      const manualPolicies = await insurancePolicyService.getUserPolicies(
        firebaseToken
      );

      return manualPolicies || [];
    } catch (error) {
      console.error("Error checking for existing policies:", error);
      throw error;
    }
  },

  /**
   * Check if a user has any expiring policies (manual)
   *
   * @param {string} userId - Firebase user ID
   * @returns {Promise<Object>} - Policy information or null if none found
   */
  checkExpiringPolicies: async (firebaseToken) => {
    try {
      // First check for manual policies
      const manualPolicies = await insurancePolicyService.getExpiringUserPolicies(
        firebaseToken
      );

      return manualPolicies || [];
    } catch (error) {
      console.error("Error checking for existing policies:", error);
      throw error;
    }
  },

  /**
   * Get the Insurify redirect URL based on current domain's campaign
   *
   * @returns {Promise<string>} - Redirect URL with slug or fallback
   */
  getInsurifyRedirectUrl: async (firebaseToken) => {
    try {
      // Determine campaign based on current domain
      const currentDomain = window.location.hostname;
      let campaign = "ownliapp";

      if (currentDomain.startsWith("dimo")) {
        campaign = "ownlidm";
      } else if (currentDomain.startsWith("bbmc")) {
        campaign = "ownlibb";
      } else if (currentDomain.startsWith("insurance.waters")) {
        campaign = "ownliwcw";
      }

      // Compose API URL
      const uri = process.env.REACT_APP_API_BASE_URL || DEFAULT_URL;
      const API_URL = uri || "https://api.ownli.co";

      const response = await fetch(
        `${API_URL}/api/insurify/get-url?campaign=${encodeURIComponent(
          campaign
        )}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-user-token": firebaseToken,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Failed to get Insurify redirect URL"
        );
      }

      const redirectUrl = await response.text(); // plain URL string
      return redirectUrl;
    } catch (error) {
      console.error("Error getting Insurify redirect URL:", error);
      throw error;
    }
  },

  /**
   * Get the Insurify redirect URL based on current domain's campaign
   *
   * @returns {Promise<string>} - Redirect URL with slug or fallback
   */
  getNovoRedirectUrl: async (firebaseToken) => {
    try {
      // Determine campaign based on current domain
      const currentDomain = window.location.hostname;
      let campaign = "ownliapp";

      if (currentDomain.startsWith("dimo")) {
        campaign = "ownlidm";
      } else if (currentDomain.startsWith("bbmc")) {
        campaign = "ownlibb";
      } else if (currentDomain.startsWith("waterscarwash")) {
        campaign = "ownliwcw";
      }

      // Compose API URL
      const uri = process.env.REACT_APP_API_BASE_URL || DEFAULT_URL;
      const API_URL = uri || "https://api.ownli.co";

      const response = await fetch(
        `${API_URL}/api/novo/get-url?campaign=${encodeURIComponent(campaign)}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-user-token": firebaseToken,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to get Novo redirect URL");
      }

      const redirectUrl = await response.text(); // plain URL string
      return redirectUrl;
    } catch (error) {
      console.error("Error getting Insurify redirect URL:", error);
      throw error;
    }
  },

  /**
   * Get the Novo ad response based on current domain's campaign
   *
   * @returns {Promise<Object>} - Novo ad response
   */
  getNovoAdResponse: async (firebaseToken, jornayaLeadId) => {
    try {
      // Determine campaign based on current domain
      const currentDomain = window.location.hostname;
      let campaign = "ownliapp";

      if (currentDomain.startsWith("dimo")) {
        campaign = "ownlidm";
      } else if (currentDomain.startsWith("bbmc")) {
        campaign = "ownlibb";
      } else if (currentDomain.startsWith("waterscarwash")) {
        campaign = "ownliwcw";
      }

      // Compose API URL
      const uri = process.env.REACT_APP_API_BASE_URL || DEFAULT_URL;
      const API_URL = uri || "https://api.ownli.co";

      const response = await fetch(
        `${API_URL}/api/novo/get-ad-response?campaign=${encodeURIComponent(
          campaign
        )}&jornayaLeadId=${jornayaLeadId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-user-token": firebaseToken,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to get Novo ad response");
      }

      const adResponse = await response.json(); // JSON response
      return adResponse;
    } catch (error) {
      console.error("Error getting Novo ad response:", error);
      throw error;
    }
  },
};

/**
 * Helper function to convert a file to base64
 *
 * @param {File} file - The file to convert
 * @returns {Promise<string>} - Base64 representation of the file
 */
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      // Remove the data URL prefix (e.g., "data:application/pdf;base64,")
      const base64 = reader.result.split(",")[1];
      resolve(base64);
    };
    reader.onerror = (error) => reject(error);
  });
};

export default insurancePolicyService;
